import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { PageBreadcrumbComponent } from '../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ButtonComponent } from '../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../shared/components/ui/badge/badge.component';
import { ModalComponent } from '../../shared/components/ui/modal/modal.component';
import { LabelComponent } from '../../shared/components/form/label/label.component';
import { InputFieldComponent } from '../../shared/components/form/input/input-field.component';
import { UbigeoPickerComponent } from '../../shared/components/form/ubigeo-picker/ubigeo-picker.component';
import { NotificationService } from '../../shared/components/ui/notification/notification.service';

import { OriginPointService } from '../../services/origin-point.service';
import { UbigeoService } from '../../services/ubigeo.service';
import { OriginPointResponse } from '../../dto/origin-point.response';
import { OriginPointRequest } from '../../dto/origin-point.request';
import { UbigeoResponse } from '../../dto/ubigeo.response';

@Component({
    selector: 'app-origin-point',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NgClass,
        PageBreadcrumbComponent,
        ButtonComponent,
        BadgeComponent,
        ModalComponent,
        LabelComponent,
        InputFieldComponent,
        UbigeoPickerComponent,
    ],
    templateUrl: './origin-point.component.html',
})
export class OriginPointComponent implements OnInit, OnDestroy {

    // Pagination
    currentPage = 1;
    itemsPerPage = 5;
    readonly pageSizeOptions = [5, 10, 15, 20, 50];

    // Sort
    sortColumn = '';
    sortDir: 'asc' | 'desc' = 'asc';

    // UI
    loading = false;
    loadingTable = false;
    searchTerm = '';

    // Data
    originPoints: OriginPointResponse[] = [];
    allUbigeos: UbigeoResponse[] = [];
    loadingUbigeos = false;
    private sub = new Subscription();

    // Form modal
    showForm = false;
    isEditMode = false;
    selectedPoint?: OriginPointResponse;

    // Confirm toggle
    showConfirmToggle = false;
    toggleTarget?: OriginPointResponse;
    isToggleStatus = false;

    // Confirm default
    showConfirmDefault = false;
    defaultTarget?: OriginPointResponse;
    isSettingDefault = false;

    // Form state
    submitted = false;
    isSubmitting = false;

    // Form model
    name = '';
    address = '';
    ubigeo = '';
    localCode = '';

    constructor(
        private originPointService: OriginPointService,
        private ubigeoService: UbigeoService,
        private notify: NotificationService,
    ) { }

    ngOnInit(): void {
        this.loadPoints();
        this.loadUbigeos();
    }

    ngOnDestroy(): void { this.sub.unsubscribe(); }

    private loadUbigeos(): void {
        this.loadingUbigeos = true;
        const s = this.ubigeoService.listActive().subscribe({
            next: res => { this.allUbigeos = res.data ?? []; this.loadingUbigeos = false; },
            error: () => { this.loadingUbigeos = false; },
        });
        this.sub.add(s);
    }

    getUbigeoLabel(code: string): string {
        if (!code) return '';
        const u = this.allUbigeos.find(x => x.ubigeo === code);
        return u ? `${u.department} / ${u.province} / ${u.distrit}` : code;
    }

    loadPoints(): void {
        this.loading = true;
        this.loadingTable = true;
        const s = this.originPointService.getAll().subscribe({
            next: res => {
                this.originPoints = res?.data ?? [];
                this.currentPage = 1;
                this.loading = false;
                this.loadingTable = false;
            },
            error: err => {
                this.loading = false;
                this.loadingTable = false;
                this.originPoints = [];
                this.notify.error(err?.error?.message ?? 'Error al listar puntos de partida.');
            },
        });
        this.sub.add(s);
    }

    setSearchTerm(value: string): void {
        this.searchTerm = (value ?? '').trim().toLowerCase();
        this.currentPage = 1;
    }

    private matchesSearch(p: OriginPointResponse): boolean {
        if (!this.searchTerm) return true;
        return (
            p.name.toLowerCase().includes(this.searchTerm) ||
            p.address.toLowerCase().includes(this.searchTerm) ||
            (p.localCode ?? '').toLowerCase().includes(this.searchTerm)
        );
    }

    get filteredPoints(): OriginPointResponse[] {
        let list = this.originPoints.filter(p => this.matchesSearch(p));
        if (this.sortColumn === 'nombre') {
            list = [...list].sort((a, b) =>
                this.sortDir === 'asc'
                    ? a.name.localeCompare(b.name, 'es')
                    : b.name.localeCompare(a.name, 'es')
            );
        }
        return list;
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredPoints.length / this.itemsPerPage));
    }

    get pageItems(): (number | '...')[] {
        const total = this.totalPages;
        const current = this.currentPage;
        const pages = new Set<number>();
        for (let i = 1; i <= Math.min(3, total); i++) pages.add(i);
        for (let i = Math.max(1, total - 2); i <= total; i++) pages.add(i);
        for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
        const sorted = Array.from(pages).sort((a, b) => a - b);
        const result: (number | '...')[] = [];
        for (let i = 0; i < sorted.length; i++) {
            if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
            result.push(sorted[i]);
        }
        return result;
    }

    get currentItems(): OriginPointResponse[] {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        return this.filteredPoints.slice(start, start + this.itemsPerPage);
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages) this.currentPage = page;
    }

    toggleSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDir = 'asc';
        }
    }

    onPageSizeChange(size: number): void {
        this.itemsPerPage = size;
        this.currentPage = 1;
    }

    getBadgeColor(status: number): 'success' | 'error' {
        return status === 1 ? 'success' : 'error';
    }

    getBadgeText(status: number): string {
        return status === 1 ? 'Activo' : 'Inactivo';
    }

    // ── Form ──────────────────────────────────────────────

    onCreate(): void {
        this.isEditMode = false;
        this.selectedPoint = undefined;
        this.resetForm();
        this.showForm = true;
    }

    onEdit(p: OriginPointResponse): void {
        this.isEditMode = true;
        this.selectedPoint = p;
        this.resetForm();
        this.name = p.name ?? '';
        this.address = p.address ?? '';
        this.ubigeo = p.ubigeo ?? '';
        this.localCode = p.localCode ?? '';
        this.showForm = true;
    }

    onCloseForm(): void {
        this.showForm = false;
        this.submitted = false;
    }

    private resetForm(): void {
        this.submitted = false;
        this.isSubmitting = false;
        this.name = '';
        this.address = '';
        this.ubigeo = '';
        this.localCode = '';
    }

    private isFormValid(): boolean {
        return !!this.name.trim() && !!this.address.trim() && !!this.ubigeo.trim();
    }

    onSubmit(): void {
        this.submitted = true;
        if (!this.isFormValid()) return;
        this.isSubmitting = true;

        const payload: OriginPointRequest = {
            name: this.name.trim(),
            address: this.address.trim(),
            ubigeo: this.ubigeo.trim(),
            localCode: this.localCode.trim() || undefined,
        };

        const request$ = this.isEditMode && this.selectedPoint?.id
            ? this.originPointService.update(this.selectedPoint.id, payload)
            : this.originPointService.create(payload);

        const s = request$.subscribe({
            next: res => {
                this.isSubmitting = false;
                this.showForm = false;
                this.resetForm();
                this.loadPoints();
                this.notify.success(res?.message ?? 'Guardado correctamente');
            },
            error: err => {
                this.isSubmitting = false;
                this.notify.error(err?.error?.message ?? 'No se pudo guardar el punto de partida.');
            },
        });
        this.sub.add(s);
    }

    // ── Toggle status ──────────────────────────────────────

    openToggleConfirm(p: OriginPointResponse): void {
        this.toggleTarget = p;
        this.showConfirmToggle = true;
    }

    closeToggleConfirm(): void {
        this.showConfirmToggle = false;
        this.toggleTarget = undefined;
    }

    get toggleActionLabel(): string {
        return this.toggleTarget?.status === 1 ? 'Inactivar' : 'Activar';
    }

    get toggleConfirmText(): string {
        if (!this.toggleTarget) return '';
        const action = this.toggleTarget.status === 1 ? 'inactivar' : 'activar';
        const extra = this.toggleTarget.status === 1 && this.toggleTarget.isDefault
            ? ' Dejará de ser el punto predeterminado.' : '';
        return `¿Deseas ${action} "${this.toggleTarget.name}"?${extra}`;
    }

    confirmToggleStatus(): void {
        if (!this.toggleTarget) return;
        this.isToggleStatus = true;
        const p = this.toggleTarget;
        const newStatus = p.status === 1 ? 0 : 1;

        const s = this.originPointService.updateStatus(p.id, newStatus).subscribe({
            next: res => {
                this.notify.success(res?.message ?? 'Estado actualizado');
                this.closeToggleConfirm();
                this.loadPoints();
                this.isToggleStatus = false;
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'No se pudo actualizar el estado.');
                this.isToggleStatus = false;
            },
        });
        this.sub.add(s);
    }

    // ── Default ────────────────────────────────────────────

    openDefaultConfirm(p: OriginPointResponse): void {
        this.defaultTarget = p;
        this.showConfirmDefault = true;
    }

    closeDefaultConfirm(): void {
        this.showConfirmDefault = false;
        this.defaultTarget = undefined;
    }

    confirmSetDefault(): void {
        if (!this.defaultTarget) return;
        this.isSettingDefault = true;
        const s = this.originPointService.setDefault(this.defaultTarget.id).subscribe({
            next: res => {
                this.notify.success(res?.message ?? 'Predeterminado actualizado');
                this.closeDefaultConfirm();
                this.loadPoints();
                this.isSettingDefault = false;
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'No se pudo marcar como predeterminado.');
                this.isSettingDefault = false;
            },
        });
        this.sub.add(s);
    }
}
